import React from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, CreditCard, Phone, MessageSquare } from 'lucide-react';

const GivePage = () => {
  const easytithUrl = 'https://app.easytithe.com/app/giving/stjo6318650';
  const textNumber = '2192555077';

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-700 to-amber-800 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Heart className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Online Giving
          </h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto">
            Support the ministry of St. John's UCC through your generous giving
          </p>
        </div>
      </section>

      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          {/* Scripture */}
          <div className="text-center mb-12">
            <blockquote className="text-xl italic text-stone-700 mb-4">
              "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver."
            </blockquote>
            <cite className="text-amber-700 font-medium">- 2 Corinthians 9:7</cite>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Online Giving */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="h-8 w-8 text-amber-700" />
                </div>
                <h2 className="text-2xl font-semibold text-stone-800 mb-4">
                  Give Online
                </h2>
                <p className="text-stone-600 mb-6">
                  Make a one-time or recurring donation securely through EasyTithe. 
                  You can give using a credit card or bank account.
                </p>
                <a href={easytithUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-amber-700 hover:bg-amber-800" size="lg" data-testid="give-online-btn">
                    Give Now
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Text to Give */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="h-8 w-8 text-amber-700" />
                </div>
                <h2 className="text-2xl font-semibold text-stone-800 mb-4">
                  Text to Give
                </h2>
                <p className="text-stone-600 mb-6">
                  Simply text the word "Give" to our giving number. You'll receive a link 
                  to complete your donation.
                </p>
                <div className="bg-stone-100 rounded-lg p-4 mb-6">
                  <p className="text-sm text-stone-500 mb-1">Text "Give" to:</p>
                  <p className="text-2xl font-bold text-amber-700">{textNumber}</p>
                </div>
                <a href={`sms:${textNumber}?body=Give`}>
                  <Button variant="outline" className="w-full border-amber-700 text-amber-700 hover:bg-amber-50" size="lg">
                    <Phone className="mr-2 h-4 w-4" />
                    Open Text Message
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Instructions */}
          <Card className="mt-8">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-stone-800 mb-4">How It Works</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800">Choose Your Method</h4>
                    <p className="text-stone-600">Click "Give Now" for online giving or text "Give" to {textNumber}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800">Select Amount & Frequency</h4>
                    <p className="text-stone-600">Choose "One-time" or "Recurring" and enter your donation amount</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800">Enter Payment Information</h4>
                    <p className="text-stone-600">Provide your credit card or bank account details</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800">Receive Confirmation</h4>
                    <p className="text-stone-600">You'll receive an email receipt acknowledging your gift</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Other Ways */}
          <div className="mt-8 text-center">
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Other Ways to Give</h3>
            <p className="text-stone-600">
              You can also give by check or cash during Sunday worship services, 
              or mail your donation to the church office.
            </p>
            <p className="text-stone-500 mt-2">
              St. John's UCC • 1288 S. Indiana Ave • Crown Point, IN 46307
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GivePage;
